"use client";

import { useLayoutEffect, useState } from "react";
import useResizeMeasure from "@/hooks/useResizeMeasure";

/**
 * ارتفاع واقعی نوارهای فیکس پایین صفحه را اندازه می‌گیرد و در یک متغیر CSS
 * روی ریشه‌ی سند می‌گذارد.
 *
 * قبلا این ارتفاع‌ها با عددهای ثابت (مثل `mb-[169px]` یا `bottom-[60px]`)
 * حساب می‌شد و هر تغییری در محتوای نوار، فاصله‌ی خالی یا هم‌پوشانی با فوتر
 * گلوبال ایجاد می‌کرد. با این هوک همه‌ی مصرف‌کننده‌ها از همان اندازه‌ی
 * اندازه‌گیری‌شده استفاده می‌کنند.
 *
 * خروجی یک callback ref است (نه useRef) تا اگر المان نوار با تاخیر رندر شود
 * (مثل نویگیشن فوتر که تا لود شدن منوها نسخه‌ی اسکلتی نشان می‌دهد) اندازه‌گیری
 * دوباره انجام شود.
 *
 * یک متغیر ممکن است چند مصرف‌کننده داشته باشد؛ بعضی صفحه‌ها نسخه‌ی موبایل و
 * دسکتاپ را با هم رندر می‌کنند و هر لحظه فقط یکی از آن‌ها دیده می‌شود (مثل
 * تب‌های صفحه‌ی علاقه‌مندی‌ها که چهار نسخه از نوار پیجینیشن می‌سازند). المان
 * مخفی ارتفاع صفر دارد و اگر هر نسخه مستقیم روی متغیر بنویسد، آخرین افکتی که
 * اجرا شود مقدار را صفر می‌کند. پس همه‌ی نوارهای یک متغیر در یک رجیستری ثبت
 * می‌شوند و بزرگ‌ترین ارتفاع (یعنی همان نوار دیده‌شده) نوشته می‌شود.
 *
 * @param {string} css_variable نام متغیر CSS، مثل `--footer-nav-height`
 * @returns {(node: HTMLElement | null) => void}
 */
const registries = new Map();

const get_registry = (css_variable) => {
	if (!registries.has(css_variable)) registries.set(css_variable, new Set());
	return registries.get(css_variable);
};

const read_height = (css_variable) =>
	Math.round(Math.max(0, ...[...get_registry(css_variable)].map((node) => node?.offsetHeight || 0)));

const write_height = (css_variable, height) =>
	document.documentElement.style.setProperty(css_variable, `${height}px`);

const apply_variable = (css_variable) => {
	if (typeof document === "undefined") return;
	write_height(css_variable, read_height(css_variable));
};

export default function useFixedBarHeight(css_variable) {
	const [node, set_node] = useState(null);

	useLayoutEffect(() => {
		if (!node || typeof document === "undefined") return;

		const registry = get_registry(css_variable);
		registry.add(node);

		return () => {
			// با خروج از صفحه، مصرف‌کننده‌ها نباید فاصله‌ی اضافه نگه دارند
			registry.delete(node);
			apply_variable(css_variable);
		};
	}, [node, css_variable]);

	// ارتفاع فقط در ResizeObserver مشترک خوانده می‌شود (useResizeMeasure): اولین callback همان اندازه‌گیری اولیه است و
	// تغییر اندازه‌ی پنجره هم (اگر ارتفاع نوار را عوض کند) از همین‌جا می‌رسد. خواندن مستقیم offsetHeight در افکت و روی
	// هر resize، layout اجباری می‌ساخت.
	useResizeMeasure(
		() => [node],
		() => {
			const height = read_height(css_variable);
			return () => write_height(css_variable, height);
		},
		[node, css_variable]
	);

	return set_node;
}

export const FOOTER_NAV_HEIGHT_VAR = "--footer-nav-height";
export const PAGE_FIXED_FOOTER_HEIGHT_VAR = "--page-fixed-footer-height";
